<template>
  <div class="form-responses custom-scroll-bar-2">
    <vue-element-loading :active="isLoading" is-full-screen />

    <div class="form-responses__toolbar">
      <button class="btn-secondary cursor-pointer" @click="goBack">← Danh sách</button>
      <div class="form-responses__heading">
        <h2 class="heading-500">{{ formTitle }}</h2>
        <p v-if="formDescription" class="text-300-default neutral_2">{{ formDescription }}</p>
      </div>
    </div>

    <div class="form-responses__tabs">
      <button
        type="button"
        class="btn btn-circle"
        :class="{ active: activeTab === 'overview' }"
        @click="activeTab = 'overview'"
      >
        Tổng quan
      </button>
      <button
        type="button"
        class="btn btn-circle ml-10px"
        :class="{ active: activeTab === 'detail' }"
        @click="activeTab = 'detail'"
      >
        Chi tiết phản hồi
      </button>
    </div>

    <!-- Overview -->
    <div v-show="activeTab === 'overview'" class="form-responses__overview">
      <div class="form-responses__kpi-row">
        <div class="form-responses__kpi-card">
          <span class="form-responses__kpi-label">Tổng phản hồi</span>
          <strong class="form-responses__kpi-value">{{ totalResponses }}</strong>
        </div>
        <div class="form-responses__kpi-card">
          <span class="form-responses__kpi-label">Trạng thái</span>
          <strong class="form-responses__kpi-value">{{ statusLabel(formStatus) }}</strong>
        </div>
        <div class="form-responses__kpi-card">
          <span class="form-responses__kpi-label">Bắt đầu</span>
          <strong class="form-responses__kpi-value">{{ formatDate(formStartsAt) }}</strong>
        </div>
        <div class="form-responses__kpi-card">
          <span class="form-responses__kpi-label">Hết hạn</span>
          <strong class="form-responses__kpi-value">{{ formatDate(formExpiresAt) }}</strong>
        </div>
      </div>

      <div v-if="!summaryFields.length && !isLoading" class="form-responses__empty-block">
        Chưa có dữ liệu phân tích.
      </div>

      <div
        v-for="item in summaryFields"
        :key="item.field_id"
        class="form-responses__analysis-card"
      >
        <div class="form-responses__analysis-head">
          <h4 class="text-400-semibold">{{ item.label }}</h4>
          <span class="text-200-default neutral_3">{{ fieldTypeLabel(item.type) }}</span>
        </div>

        <!-- Choice / dropdown -->
        <template v-if="isChoiceField(item.type)">
          <div
            v-for="opt in choiceBreakdown(item)"
            :key="opt.id"
            class="form-responses__bar-row"
          >
            <span class="form-responses__bar-label text-300-default">{{ opt.label }}</span>
            <div class="form-responses__bar-track">
              <div class="form-responses__bar-fill" :style="{ width: opt.percent + '%' }" />
            </div>
            <span class="form-responses__bar-stat text-300-default">
              {{ opt.count }} ({{ opt.percent }}%)
            </span>
          </div>
        </template>

        <!-- Rating -->
        <template v-else-if="item.type === 'rating'">
          <p class="text-300-default mb-10px">
            Trung bình:
            <strong>{{ item.average != null ? item.average : "—" }}</strong>
            / {{ ratingMax(item) }}
            · {{ item.count || 0 }} lượt trả lời
          </p>
          <div
            v-for="row in ratingBreakdown(item)"
            :key="row.star"
            class="form-responses__bar-row"
          >
            <span class="form-responses__bar-label text-300-default">{{ row.star }} sao</span>
            <div class="form-responses__bar-track">
              <div class="form-responses__bar-fill form-responses__bar-fill--rating" :style="{ width: row.percent + '%' }" />
            </div>
            <span class="form-responses__bar-stat text-300-default">
              {{ row.count }} ({{ row.percent }}%)
            </span>
          </div>
        </template>

        <!-- Text / other -->
        <template v-else>
          <p class="text-300-default mb-8px">
            {{ item.sample_count || 0 }} câu trả lời có nội dung
          </p>
          <ul v-if="item.latest && item.latest.length" class="form-responses__samples">
            <li v-for="(sample, index) in item.latest" :key="index">
              {{ formatSample(item.field_id, sample) }}
            </li>
          </ul>
          <p v-else class="text-200-default neutral_3">Chưa có câu trả lời.</p>
        </template>
      </div>
    </div>

    <!-- Detail table -->
    <div v-show="activeTab === 'detail'">
      <div v-if="tableFields.length" class="form-responses__filters">
        <div class="form-responses__filters-head">
          <span class="text-400-semibold">Bộ lọc</span>
          <button
            v-if="hasActiveFilters"
            type="button"
            class="btn-link cursor-pointer"
            @click="clearFieldFilters"
          >
            Xóa bộ lọc
          </button>
        </div>
        <div class="form-responses__filters-grid">
          <div
            v-for="field in tableFields"
            :key="'filter-' + field.id"
            class="form-responses__filter-item"
          >
            <label class="text-300-default neutral_2">{{ field.label }}</label>
            <select
              v-model="fieldFilters[field.id]"
              class="form-control input-custom w_100"
              @change="onFilterChange"
            >
              <option
                v-for="opt in filterOptionsForField(field)"
                :key="field.id + '-opt-' + opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>
        <p class="text-200-default neutral_3 form-responses__filter-summary">
          Hiển thị {{ filteredResponses.length }} / {{ allResponses.length }} phản hồi
        </p>
      </div>

      <div class="form-responses__table-wrap">
        <table class="ml-member-tbl form-responses__table">
          <thead>
            <tr>
              <th
                class="text-400-semibold ml-table-cell w_min_160 form-responses__sortable"
                @click="toggleSort('user.fullname')"
              >
                Người gửi
                <span class="form-responses__sort-icon">{{ sortIcon("user.fullname") }}</span>
              </th>
              <th
                class="text-400-semibold ml-table-cell w_min_160 form-responses__sortable"
                @click="toggleSort('user.role')"
              >
                Chức danh
                <span class="form-responses__sort-icon">{{ sortIcon("user.role") }}</span>
              </th>
              <th
                class="text-400-semibold ml-table-cell w_min_160 form-responses__sortable"
                @click="toggleSort('submitted_at')"
              >
                Thời gian
                <span class="form-responses__sort-icon">{{ sortIcon("submitted_at") }}</span>
              </th>
              <th
                v-for="field in tableFields"
                :key="field.id"
                class="text-400-semibold ml-table-cell w_min_180 form-responses__sortable"
                @click="toggleSort(field.id)"
              >
                {{ field.label }}
                <span class="form-responses__sort-icon">{{ sortIcon(field.id) }}</span>
              </th>
              <th class="text-400-semibold ml-table-cell w_min_120">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!paginatedResponses.length && !isLoading">
              <td :colspan="4 + tableFields.length" class="text-center form-responses__empty-cell">
                {{ allResponses.length ? "Không có phản hồi khớp bộ lọc." : "Chưa có phản hồi." }}
              </td>
            </tr>
            <tr v-for="row in paginatedResponses" :key="row.id">
              <td class="text-400-medium ml-table-cell">
                {{ row.user ? row.user.fullname : "—" }}
              </td>
              <td class="text-400-medium ml-table-cell">
                {{ userRoleDisplay(row) }}
              </td>
              <td class="text-400-medium ml-table-cell">{{ formatDate(row.submitted_at) }}</td>
              <td
                v-for="field in tableFields"
                :key="field.id + '-' + row.id"
                class="text-400-medium ml-table-cell form-responses__answer-cell"
              >
                <span v-if="field.type === 'rating'" class="form-responses__stars">
                  <template v-if="ratingValue(field, row) > 0">
                    <span
                      v-for="n in (field.max || 5)"
                      :key="n"
                      class="form-responses__star"
                      :class="{ active: n <= ratingValue(field, row) }"
                    >★</span>
                  </template>
                  <span v-else class="neutral_3">—</span>
                </span>
                <span v-else>{{ cellAnswer(field, row) }}</span>
              </td>
              <td class="text-400-medium ml-table-cell form-responses__actions">
                <button
                  type="button"
                  class="btn-action cursor-pointer"
                  :disabled="!row.user || !row.user.id || messagingUserId === row.user.id"
                  @click="messageUser(row)"
                >
                  {{ messagingUserId === row.user.id ? "Đang mở..." : "Nhắn tin" }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <pagination-template
        v-if="filteredResponses.length > 0"
        class="form-responses__pagination"
        :params="filteredPagination"
        @change-params="changePage"
      />
    </div>
  </div>
</template>

<script>
import AdminFormService from "../../../../store/services/AdminFormService";
import PaginationTemplate from "../../../Components/PaginationTemplate";
import {
  FORM_STATUS_LABELS,
  fieldTypeLabel,
  formatFormDate,
} from "../../../../helper/formSchema";
import {
  buildChoiceBreakdown,
  buildFieldMap,
  buildFormResponseComposeText,
  formatAnswerValue,
  getInputFields,
} from "../../../../helper/formAnswerDisplay";
import {
  createEmptyFieldFilters,
  filterOptionsForField,
  matchesAllFieldFilters,
} from "../../../../helper/formResponseFilters";
import {
  compareResponseRows,
  defaultSortDirectionForKey,
} from "../../../../helper/formResponseSort";
import { openPrivateChatWithDraft } from "../../../../helper/openPrivateChat";

export default {
  name: "FormResponses",
  components: { PaginationTemplate },
  data() {
    return {
      formId: null,
      formTitle: "",
      formDescription: "",
      formStatus: "",
      formStartsAt: null,
      formExpiresAt: null,
      formDefinition: null,
      fieldMap: {},
      summary: null,
      allResponses: [],
      fieldFilters: {},
      isLoading: false,
      messagingUserId: null,
      activeTab: "overview",
      sortState: { key: "submitted_at", direction: "desc" },
      pagination: { page: 1, perPage: 50, lastPage: 1, total: 0 },
    };
  },
  computed: {
    totalResponses() {
      return (this.summary && this.summary.total_responses) || 0;
    },
    summaryFields() {
      return (this.summary && this.summary.fields) || [];
    },
    tableFields() {
      return getInputFields(this.formDefinition);
    },
    filteredResponses() {
      return this.allResponses.filter((row) =>
        matchesAllFieldFilters(row, this.tableFields, this.fieldFilters),
      );
    },
    sortedResponses() {
      const rows = this.filteredResponses.slice();
      const key = this.sortState.key;
      const direction = this.sortState.direction;
      rows.sort((a, b) => compareResponseRows(a, b, key, direction, this.fieldMap));
      return rows;
    },
    paginatedResponses() {
      const start = (this.pagination.page - 1) * this.pagination.perPage;
      return this.sortedResponses.slice(start, start + this.pagination.perPage);
    },
    filteredPagination() {
      const total = this.filteredResponses.length;
      const lastPage = Math.max(1, Math.ceil(total / this.pagination.perPage) || 1);
      return {
        page: Math.min(this.pagination.page, lastPage),
        perPage: this.pagination.perPage,
        lastPage,
        total,
      };
    },
    hasActiveFilters() {
      return Object.values(this.fieldFilters).some((value) => !!value);
    },
  },
  created() {
    this.formId = this.$route.params.form_id;
    this.loadAll();
  },
  methods: {
    formatDate: formatFormDate,
    fieldTypeLabel(type) {
      return fieldTypeLabel(type);
    },
    statusLabel(key) {
      return FORM_STATUS_LABELS[key] || key || "—";
    },
    goBack() {
      this.$router.push("/admin-settings/forms");
    },
    isChoiceField(type) {
      return ["single_choice", "multi_choice", "dropdown"].includes(type);
    },
    filterOptionsForField(field) {
      return filterOptionsForField(field);
    },
    onFilterChange() {
      this.pagination.page = 1;
    },
    toggleSort(key) {
      if (this.sortState.key === key) {
        this.sortState.direction = this.sortState.direction === "asc" ? "desc" : "asc";
      } else {
        this.sortState = {
          key,
          direction: defaultSortDirectionForKey(key),
        };
      }
      this.pagination.page = 1;
    },
    sortIcon(key) {
      if (this.sortState.key !== key) return "↕";
      return this.sortState.direction === "asc" ? "↑" : "↓";
    },
    clearFieldFilters() {
      this.fieldFilters = createEmptyFieldFilters(this.formDefinition);
      this.pagination.page = 1;
    },
    userRoleDisplay(row) {
      const roles = row.user && row.user.roles;
      if (!Array.isArray(roles) || !roles.length) return "—";
      const labels = roles
        .map((role) => role.displayName || role.display_name)
        .filter(Boolean);
      return labels.length ? labels.join(", ") : "—";
    },
    ratingValue(field, row) {
      const answers = row.answers || {};
      const raw = answers[field.id];
      if (raw === null || raw === undefined || raw === "") return 0;
      const value = Math.round(Number(raw));
      return Number.isNaN(value) ? 0 : value;
    },
    choiceBreakdown(item) {
      if (Array.isArray(item.counts_labeled) && item.counts_labeled.length) {
        return item.counts_labeled;
      }
      const field = this.fieldMap[item.field_id];
      if (!field) return [];
      return buildChoiceBreakdown(field, item.counts, this.totalResponses);
    },
    ratingMax(item) {
      return item.max || this.ratingMaxFromDefinition(item.field_id);
    },
    ratingMaxFromDefinition(fieldId) {
      const field = this.fieldMap[fieldId];
      return (field && field.max) || 5;
    },
    ratingBreakdown(item) {
      if (Array.isArray(item.distribution) && item.distribution.length) {
        return item.distribution;
      }
      const field = this.fieldMap[item.field_id];
      if (!field) return [];
      return [];
    },
    formatSample(fieldId, sample) {
      return formatAnswerValue(this.fieldMap[fieldId], sample);
    },
    cellAnswer(field, row) {
      const answers = row.answers || {};
      return formatAnswerValue(field, answers[field.id]);
    },
    async messageUser(row) {
      if (!row || !row.user || !row.user.id) return;
      this.messagingUserId = row.user.id;
      try {
        const draft = buildFormResponseComposeText(row, this.formDefinition);
        const result = await openPrivateChatWithDraft(
          this.$store,
          this.$router,
          {
            id: row.user.id,
            fullname: row.user.fullname,
            email: row.user.email,
          },
          draft,
        );
        if (!result.ok) {
          this.$notify({
            type: "error",
            text: result.message || "Không mở được cuộc trò chuyện.",
          });
        }
      } finally {
        this.messagingUserId = null;
      }
    },
    applyFormDetail(data) {
      if (!data) return;
      this.formTitle = data.title || "Biểu mẫu";
      this.formDescription = (data.definition && data.definition.description) || "";
      this.formStatus = data.effective_status || data.status || "";
      this.formStartsAt = data.starts_at;
      this.formExpiresAt = data.expires_at;
      this.formDefinition = data.definition || null;
      this.fieldMap = buildFieldMap(this.formDefinition);
      this.fieldFilters = createEmptyFieldFilters(this.formDefinition);
    },
    async fetchAllResponses() {
      let page = 1;
      let lastPage = 1;
      const items = [];

      do {
        const listRes = await AdminFormService.responses(this.formId, {
          page,
          per_page: 100,
        });
        if (!listRes || !listRes.data) break;
        const batch = listRes.data.items || [];
        items.push(...batch);
        const paging = listRes.data.pagination || {};
        lastPage = paging.last_page || 1;
        page += 1;
      } while (page <= lastPage);

      this.allResponses = items;
      this.pagination.total = items.length;
      this.pagination.lastPage = Math.max(
        1,
        Math.ceil(items.length / this.pagination.perPage) || 1,
      );
    },
    async loadAll() {
      this.isLoading = true;
      try {
        const [detailRes, summaryRes] = await Promise.all([
          AdminFormService.getById(this.formId),
          AdminFormService.summary(this.formId),
        ]);

        if (detailRes && detailRes.data) {
          this.applyFormDetail(detailRes.data);
        }
        if (summaryRes && summaryRes.data) {
          this.summary = summaryRes.data;
        }
        await this.fetchAllResponses();
      } finally {
        this.isLoading = false;
      }
    },
    changePage(params) {
      if (params && params.page != null) {
        this.pagination.page = Number(params.page) || 1;
      }
      if (params && params.perPage != null) {
        this.pagination.perPage = Number(params.perPage) || 50;
        this.pagination.page = 1;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.form-responses {
  padding: 16px;
}

.form-responses__toolbar {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.form-responses__heading {
  min-width: 0;
}

.form-responses__tabs {
  margin-bottom: 16px;
}

.form-responses__kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

@media (max-width: 960px) {
  .form-responses__kpi-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.form-responses__kpi-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px;
}

.form-responses__kpi-label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
}

.form-responses__kpi-value {
  font-size: 18px;
  color: #111827;
}

.form-responses__analysis-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
}

.form-responses__analysis-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.form-responses__bar-row {
  display: grid;
  grid-template-columns: minmax(120px, 220px) 1fr 90px;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
}

.form-responses__bar-track {
  height: 10px;
  background: #f3f4f6;
  border-radius: 999px;
  overflow: hidden;
}

.form-responses__bar-fill {
  height: 100%;
  background: #2563eb;
  border-radius: 999px;
  min-width: 0;
}

.form-responses__bar-fill--rating {
  background: #f59e0b;
}

.form-responses__bar-stat {
  text-align: right;
  white-space: nowrap;
}

.form-responses__samples {
  margin: 0;
  padding-left: 18px;
}

.form-responses__table-wrap {
  overflow: auto;
}

.form-responses__sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    background: #f9fafb;
  }
}

.form-responses__sort-icon {
  margin-left: 4px;
  font-size: 12px;
  color: #6b7280;
}

.form-responses__filters {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
}

.form-responses__filters-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.form-responses__filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.form-responses__filter-item label {
  display: block;
  margin-bottom: 4px;
}

.form-responses__filter-summary {
  margin: 12px 0 0;
}

.btn-link {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 13px;
  padding: 0;
}

.form-responses__stars {
  display: inline-flex;
  gap: 1px;
  line-height: 1;
}

.form-responses__star {
  color: #d1d5db;
  font-size: 16px;

  &.active {
    color: #f59e0b;
  }
}

.form-responses__answer-cell {
  white-space: pre-wrap;
  max-width: 280px;
}

.form-responses__empty-block,
.form-responses__empty-cell {
  padding: 24px;
  color: #6b7280;
  text-align: center;
}

.form-responses__pagination {
  margin-top: 12px;
}

.form-responses__actions {
  white-space: nowrap;
}

.btn-action {
  border: 1px solid #d8dae5;
  background: #fff;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 13px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-secondary {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  flex-shrink: 0;
}
</style>
